/**
 * Number formatting utility
 * Western: K, M, B, T, Qa, Qi, Sx, Sp, Oc, No, Dc (3-digit grouping)
 * CJK (ko/ja/zh-TW): 만, 억, 조, 경, 해, ... (4-digit grouping)
 */

import { t } from '../i18n/index.js';
import { getCurrentLocale } from '../i18n/index.js';

const WESTERN_SUFFIXES = [
  { threshold: 1e33, key: 'num_dc' },
  { threshold: 1e30, key: 'num_no' },
  { threshold: 1e27, key: 'num_oc' },
  { threshold: 1e24, key: 'num_sp' },
  { threshold: 1e21, key: 'num_sx' },
  { threshold: 1e18, key: 'num_qi' },
  { threshold: 1e15, key: 'num_qa' },
  { threshold: 1e12, key: 'num_t' },
  { threshold: 1e9, key: 'num_b' },
  { threshold: 1e6, key: 'num_m' },
  { threshold: 1e3, key: 'num_k' },
];

// CJK uses 4-digit grouping: 만(1e4), 억(1e8), 조(1e12), 경(1e16), ...
const CJK_SUFFIXES = [
  { threshold: 1e44, key: 'num_dc' },   // 재
  { threshold: 1e40, key: 'num_no' },   // 정
  { threshold: 1e36, key: 'num_oc' },   // 간
  { threshold: 1e32, key: 'num_sp' },   // 구
  { threshold: 1e28, key: 'num_sx' },   // 양
  { threshold: 1e24, key: 'num_qi' },   // 자
  { threshold: 1e20, key: 'num_qa' },   // 해
  { threshold: 1e16, key: 'num_t' },    // 경
  { threshold: 1e12, key: 'num_b' },    // 조
  { threshold: 1e8, key: 'num_m' },     // 억
  { threshold: 1e4, key: 'num_k' },     // 만
];

const CJK_LOCALES = new Set(['ko', 'ja', 'zh-TW']);

function getSuffixes() {
  return CJK_LOCALES.has(getCurrentLocale()) ? CJK_SUFFIXES : WESTERN_SUFFIXES;
}

function getMinThreshold() {
  return CJK_LOCALES.has(getCurrentLocale()) ? 10000 : 1000;
}

/**
 * Format a number with appropriate suffix
 * Western: 1234 -> "1.23K", CJK: 12345 -> "1.23만"
 */
export function formatNumber(n: number): string {
  if (n < 0) return '-' + formatNumber(-n);
  const minT = getMinThreshold();
  if (n < minT) return Math.floor(n).toString();

  const suffixes = getSuffixes();
  for (const { threshold, key } of suffixes) {
    if (n >= threshold) {
      const val = n / threshold;
      const suffix = t(key);
      if (val >= 100) return Math.floor(val) + suffix;
      if (val >= 10) return val.toFixed(1) + suffix;
      return val.toFixed(2) + suffix;
    }
  }

  return Math.floor(n).toString();
}

/**
 * Format a number as integer with commas
 */
export function formatInteger(n: number): string {
  return Math.floor(n).toLocaleString();
}

/**
 * Format time in hours and minutes
 */
export function formatTime(ms: number): string {
  const totalSec = Math.floor(ms / 1000);
  const hours = Math.floor(totalSec / 3600);
  const minutes = Math.floor((totalSec % 3600) / 60);

  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  if (minutes > 0) {
    return `${minutes}m`;
  }
  return `${totalSec}s`;
}

/**
 * Format CPS with "/s" suffix
 */
export function formatCps(cps: number): string {
  return formatNumber(cps) + t('hud_cps');
}

/**
 * Format cooldown remaining time
 */
export function formatCooldown(remainMs: number): string {
  const sec = Math.ceil(remainMs / 1000);
  if (sec >= 60) {
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  }
  return `${sec}s`;
}
